import React from 'react';

import FlexibleDataEntryTablePreview from 'notebook/ai-tools/flexible-data-entry/FlexibleDataEntryTablePreview';
import ReviewAndConfirmResultsLoading from 'notebook/ai-tools/flexible-data-entry/ReviewAndConfirmResultsLoading';
import {
  FillStructuredTablePartialResponse,
  FillStructuredTableUsingAIResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {ModalButton} from 'ui-library/modals/Modal';

import {ModelEdits} from './FlexibleDataEntryResultEdits';

/**
 * Return modal-level buttons to show when this step is active.
 */
export function getReviewAndConfirmResultsModalButtons(
  wasVerified: boolean,
  wasSuccessful: boolean,
  isCompleted: boolean,
  handleStartOver: () => void,
  handleInsert: () => void,
  currentTableIndex: number,
  totalTables: number
): Array<ModalButton> {
  const isLastTable = currentTableIndex === totalTables - 1;
  const isFirstTable = currentTableIndex === 0;
  let submitButtonText;
  let submitButtonStyle = ButtonStyle.PRIMARY;
  const metadata = `${totalTables > 1 ? `(${currentTableIndex + 1}/${totalTables}) ` : ''}`;
  if (wasVerified) {
    submitButtonText = `${metadata}Approve and ${isLastTable ? 'insert rows' : 'continue'}`;
  } else if (wasSuccessful) {
    submitButtonText = `${metadata}Acknowledge verification failure and ${isLastTable ? 'insert rows' : 'continue'}`;
    submitButtonStyle = ButtonStyle.NEGATIVE;
  } else if (isCompleted) {
    submitButtonText = 'Close';
  } else {
    submitButtonText = `${metadata}Approve and ${isLastTable ? 'insert rows' : 'continue'}`;
  }

  return [
    {
      text: isFirstTable ? 'Discard and go back' : 'Go back to previous table',
      style: ButtonStyle.TERTIARY_GRAY,
      // Ideally we should support cancellation, but skip that for now in order to simplify the
      // state management for concurrent requests.
      disabled: !isCompleted,
      onClick: handleStartOver,
    },
    {
      text: submitButtonText,
      style: submitButtonStyle,
      disabled: !isCompleted,
      onClick: handleInsert,
      dataTestAttrs: {component: 'AIFillStructuredTableModal', element: 'approveButton'},
    },
  ];
}

interface ReviewAndConfirmResultsStepProps {
  serverErrorMessage: string | null;
  wasSuccessful: boolean;
  fillResult: FillStructuredTableUsingAIResponse | null;
  structuredTable?: StructuredTable<unknown, unknown>;
  selectedResultIndex: number;
  onSelectResultIndex: (index: number) => void;
  modelEdits: ModelEdits;
  onCellEdit: (modelIndex: number, rowIndex: number, columnIndex: number, newValue: string | null) => void;
  fillStructuredTableStatus: FillStructuredTablePartialResponse | null;
}

export default function ReviewAndConfirmResultsStep({
  serverErrorMessage,
  wasSuccessful,
  fillResult,
  structuredTable,
  selectedResultIndex,
  onSelectResultIndex,
  modelEdits,
  onCellEdit,
  fillStructuredTableStatus,
}: ReviewAndConfirmResultsStepProps): JSX.Element {
  const hadPrimaryError = fillResult?.runResults[0]?.hadError ?? false;
  const isCompleted = fillResult != null || serverErrorMessage != null;
  const isLoading = !isCompleted;

  if (isLoading) {
    return <ReviewAndConfirmResultsLoading fillStructuredTableStatus={fillStructuredTableStatus} />;
  } else if (serverErrorMessage != null) {
    // The whole operation failed, e.g. a 400 error.
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={serverErrorMessage} />;
  } else if (hadPrimaryError) {
    // We got a response back, but the primary model indicated failure (e.g. malformed LLM output).
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Operation failed. Please try again." />;
  } else if (wasSuccessful) {
    // The primary model produced non-empty results, so show the rich preview.
    return structuredTable != null ? (
      <FlexibleDataEntryTablePreview
        structuredTable={structuredTable}
        fillResult={fillResult!}
        selectedResultIndex={selectedResultIndex}
        onSelectResultIndex={onSelectResultIndex}
        modelEdits={modelEdits}
        onCellEdit={onCellEdit}
      />
    ) : (
      <InlineBanner bannerType={InlineBannerType.ERROR} text="Operation failed. Table not found." />
    );
  } else {
    // The primary model succeeded with a blank result.
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No new rows were generated." />;
  }
}
